import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Loader2, Mail, Phone } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useUI } from "@/components/Layout";

export default function UsersPanel() {
  const qc = useQueryClient();
  const { confirmAction } = useUI();
  const [q, setQ] = useState("");
  const [bannedOnly, setBannedOnly] = useState(false);
  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ["admin-users", q, bannedOnly],
    queryFn: async () => (await api.get(`/admin/users?q=${encodeURIComponent(q)}&banned=${bannedOnly}`)).data,
  });

  const toggleBan = async (u) => {
    if (!u.banned && !(await confirmAction({ title: `Ban ${u.name}?`, description: "They will be signed out everywhere and can't log in until unbanned.", actionLabel: "Ban user" }))) return;
    try {
      await api.post(`/admin/users/${u.user_id}/${u.banned ? "unban" : "ban"}`, u.banned ? undefined : { note: "" });
      toast.success(u.banned ? `${u.name} unbanned` : `${u.name} banned`);
      refetch();
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      qc.invalidateQueries({ queryKey: ["admin-log"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pl-10 rounded-xl bg-input border-border" />
        </div>
        <button data-testid="admin-banned-only" onClick={() => setBannedOnly((b) => !b)} className={`px-3 rounded-xl text-xs font-semibold ${bannedOnly ? "bg-red-600 text-white" : "bg-secondary text-muted-foreground"}`}>Banned only</button>
      </div>
      {isLoading ? <Loader2 className="w-6 h-6 animate-spin text-[#8012C6] mx-auto" /> : (
        <div className="bg-card border border-border rounded-2xl divide-y divide-border">
          {data.map((u) => (
            <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="flex items-center gap-3 p-3">
              <Avatar user={u} size={38} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                  {u.name}{u.is_admin && <span className="text-[10px] px-1.5 rounded bg-[#6C0BA9] text-white">ADMIN</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate flex items-center gap-2">
                  {u.email}
                  <Mail className={`w-3 h-3 ${u.email_verified ? "text-emerald-400" : "opacity-30"}`} />
                  <Phone className={`w-3 h-3 ${u.phone_verified ? "text-emerald-400" : "opacity-30"}`} />
                  {u.reports_against > 0 && <span className="text-amber-300">{u.reports_against} reports</span>}
                </div>
              </div>
              {!u.is_admin && (
                <Button data-testid={`admin-toggle-ban-${u.user_id}`} size="sm" onClick={() => toggleBan(u)} variant={u.banned ? "outline" : "default"} className={`rounded-xl ${u.banned ? "" : "bg-red-600 hover:bg-red-700 text-white"}`}>
                  {u.banned ? "Unban" : "Ban"}
                </Button>
              )}
            </div>
          ))}
          {data.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No users found.</div>}
        </div>
      )}
    </div>
  );
}
